import React, { useMemo, useState } from "react";
import { Box, Typography, Stack, ButtonBase } from "@mui/material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";

const T = {
  bg: "#ffffff",
  surface: "#f8f9fc",
  surfaceHover: "#f0f2f7",
  border: "rgba(0,0,0,0.07)",
  borderStrong: "rgba(0,0,0,0.13)",
  text: "#1a1d23",
  textMuted: "#6b7280",
  textFaint: "#9ca3af",
  accent: "#2563eb",
  accentGlow: "rgba(37,99,235,0.04)",
  chipActive: "rgba(37,99,235,0.1)",
  chipActiveBorder: "rgba(37,99,235,0.35)",
};

export default function TopAreasPanel({
  items = [],
  onSelect,
  onHoverChange,
  hoveredId,
  initialCount = 10,
  loading = false,
  compareMode = false,
}) {
  const [count] = useState(initialCount);

  const filteredItems = useMemo(
    () =>
      (items || [])
        .filter((i) => Number.isFinite(Number(i?.score)))
        .sort((a, b) => {
          if (compareMode) {
            const ad = Math.abs(Number(a?.delta) || 0);
            const bd = Math.abs(Number(b?.delta) || 0);
            return bd - ad;
          }
          return Number(b?.score) - Number(a?.score);
        }),
    [items, compareMode]
  );

  const visible = useMemo(() => filteredItems.slice(0, count), [filteredItems, count]);

  if (loading) {
    return (
      <Box sx={{
        py: 2.5, px: 2, mx: 2.5, borderRadius: "10px",
        border: `1px solid ${T.border}`, bgcolor: T.surface, textAlign: "center",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 1.2,
      }}>
        <Box sx={{
          width: 18, height: 18, borderRadius: "50%",
          border: "2px solid rgba(0,0,0,0.15)",
          borderTopColor: T.accent, animation: "spin 0.9s linear infinite",
        }} />
        <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
          Updating top areas…
        </Typography>
      </Box>
    );
  }

  if (filteredItems.length === 0) {
    return (
      <Box sx={{
        py: 2.5, px: 2, mx: 2.5, borderRadius: "10px",
        border: `1px dashed ${T.border}`, bgcolor: T.surface, textAlign: "center",
      }}>
        <PlaceOutlinedIcon sx={{ fontSize: 20, color: T.textFaint, mb: 0.5 }} />
        <Typography sx={{ fontSize: "12px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
          No data yet — select a variable.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2.5, pb: 1.5, fontFamily: "'DM Sans', sans-serif" }}>
      <Box sx={{
        borderRadius: "10px", border: `1px solid ${T.border}`,
        overflow: "auto", bgcolor: T.bg, maxHeight: 260,
      }}>
        {visible.map((area, i) => {
          const isHovered = hoveredId != null && String(hoveredId) === String(area.id);
          return (
            <ButtonBase
              key={area.id ?? i}
              onClick={() => onSelect?.(area)}
              onMouseEnter={() => onHoverChange?.(area.id)}
              onMouseLeave={() => onHoverChange?.(null)}
              sx={{
                width: "100%", display: "flex", alignItems: "center",
                justifyContent: "space-between", px: 1.75, py: 1,
                borderBottom: i < visible.length - 1 ? `1px solid ${T.border}` : "none",
                bgcolor: isHovered ? T.accentGlow : "transparent",
                transition: "background 0.12s",
                "&:hover": { bgcolor: T.accentGlow },
                "&:hover .area-rank": { color: T.accent },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  className="area-rank"
                  sx={{
                    fontSize: "10px", fontWeight: 700,
                    color: isHovered ? T.accent : T.textFaint,
                    fontFamily: "'DM Sans', sans-serif", minWidth: 18, transition: "color 0.12s",
                  }}
                >
                  {i + 1}
                </Typography>
                {area.color && (
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: area.color, flexShrink: 0 }} />
                )}
                <Typography sx={{
                  fontSize: "13px", fontWeight: 500, color: T.text,
                  fontFamily: "'DM Sans', sans-serif",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {area.name}
                </Typography>
              </Stack>
              {(area.score != null || Number.isFinite(Number(area?.delta))) && (
                <Typography sx={{
                  fontSize: "11px", fontWeight: 700, color: T.accent,
                  fontFamily: "'DM Sans', sans-serif", ml: 1, flexShrink: 0,
                }}>
                  {compareMode && Number.isFinite(Number(area?.delta))
                    ? `${Number(area.delta) > 0 ? "+" : ""}${Number(area.delta).toFixed(1)}pp`
                    : (typeof area.score === "number"
                        ? `${area.score.toFixed(1)}%`
                        : area.score)}
                </Typography>
              )}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}
